/**
 * Pure grouping logic for the publication schedule block.
 *
 * Publication items carry an optional `scheduledAt` instant. The schedule block
 * groups them by calendar day (local time) and derives per-day view-model rows
 * used by {@link PublicationSection}. Kept free of JSX so it is unit-testable.
 */

import { formatLocalDate } from "./FormatLocalDate";
import type { PublicationItemInfo } from "./useTaskDetailSubscription";

export interface ScheduleItemInput {
  scheduledAt: string | null;
  status: PublicationItemInfo["status"];
}

export interface ScheduleDayRow {
  /** Day of the launch, formatted ДД.ММ. */
  dateLabel: string;
  /** Launch time, formatted ЧЧ:ММ. */
  timeLabel: string;
  /** Number of items scheduled on this day. */
  scheduledCount: number;
  /** Number of items on this day already published. */
  publishedCount: number;
  /** The launch moment is in the past. */
  isPast: boolean;
  /** The earliest future launch — the next one to run. */
  isUpcoming: boolean;
}

interface DayGroup {
  launchAt: Date;
  scheduledCount: number;
  publishedCount: number;
}

function formatDayLabel(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}`;
}

function formatTimeLabel(date: Date): string {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

export function groupItemsByScheduleDay(
  items: readonly ScheduleItemInput[],
  now: Date,
): ScheduleDayRow[] {
  const groups = new Map<string, DayGroup>();
  for (const item of items) {
    if (item.scheduledAt === null) {
      continue;
    }
    const scheduledAt = new Date(item.scheduledAt);
    const key = formatLocalDate(scheduledAt);
    const existing = groups.get(key);
    const isPublished = item.status === "published";
    if (existing === undefined) {
      groups.set(key, {
        launchAt: scheduledAt,
        scheduledCount: 1,
        publishedCount: isPublished ? 1 : 0,
      });
    } else {
      existing.scheduledCount += 1;
      if (isPublished) {
        existing.publishedCount += 1;
      }
      if (scheduledAt.getTime() < existing.launchAt.getTime()) {
        existing.launchAt = scheduledAt;
      }
    }
  }

  const nowMs = now.getTime();
  const sorted = [...groups.values()].sort(
    (left, right) => left.launchAt.getTime() - right.launchAt.getTime(),
  );
  const firstUpcomingIndex = sorted.findIndex((group) => group.launchAt.getTime() >= nowMs);

  const rows = sorted.map((group, index) => ({
    dateLabel: formatDayLabel(group.launchAt),
    timeLabel: formatTimeLabel(group.launchAt),
    scheduledCount: group.scheduledCount,
    publishedCount: group.publishedCount,
    isPast: group.launchAt.getTime() < nowMs,
    isUpcoming: index === firstUpcomingIndex,
  }));
  return rows;
}
